/**
 * Manage Custom Countdown Timer
 * Supports:
 *  - Digit-box layout (blocks/countdown.liquid) via [data-day_1], etc.
 *  - Compact badge layout (promo-countdown) via [data-badge-days], etc.
 * Layouts are isolated: digit-box is checked first and returns early.
 */
class Countdown extends HTMLElement {
  constructor() {
    super();
    this.countDownTimer = null;
    this.endMs = null;
    this.endDate = null;
    this.refs = null;
  }

  connectedCallback() {
    // data-section_timer (underscore) → dataset.section_timer, not sectionTimer
    this.endDate =
      this.getAttribute('data-section_timer') ||
      this.dataset.section_timer ||
      this.dataset.sectionTimer ||
      '';

    const parsedMs = Number(this.getAttribute('data-end-ms'));
    this.endMs = Number.isFinite(parsedMs) && parsedMs > 0 ? parsedMs : null;

    if (!this.endMs && !this.endDate) return;

    this.refs = this.cacheRefs();
    this.startTimer();
  }

  disconnectedCallback() {
    this.stopTimer();
    this.refs = null;
  }

  /**
   * Scoped query — prefers theme Utility, falls back to native querySelector.
   * @param {string} selector
   * @returns {Element|null}
   */
  $(selector) {
    if (typeof Utility !== 'undefined' && Utility.DomSelector?.$) {
      return Utility.DomSelector.$(selector, this);
    }
    return this.querySelector(selector);
  }

  /**
   * Cache layout-specific nodes once (avoids querying every tick).
   * @returns {{ type: 'digit' | 'badge' | 'none', nodes: Object }}
   */
  cacheRefs() {
    const day1 = this.$('[data-day_1]');
    if (day1) {
      return {
        type: 'digit',
        nodes: {
          day1,
          day2: this.$('[data-day_2]'),
          hour1: this.$('[data-hour_1]'),
          hour2: this.$('[data-hour_2]'),
          min1: this.$('[data-min_1]'),
          min2: this.$('[data-min_2]'),
          sec1: this.$('[data-sec_1]'),
          sec2: this.$('[data-sec_2]')
        }
      };
    }

    const badgeDays = this.$('[data-badge-days]');
    const badgeHours = this.$('[data-badge-hours]');
    const badgeMins = this.$('[data-badge-mins]');
    if (badgeDays && badgeHours && badgeMins) {
      return {
        type: 'badge',
        nodes: { badgeDays, badgeHours, badgeMins }
      };
    }

    return { type: 'none', nodes: {} };
  }

  startTimer() {
    this.stopTimer();
    this.tick();
    this.countDownTimer = setInterval(() => this.tick(), 1000);
  }

  stopTimer() {
    if (this.countDownTimer) {
      clearInterval(this.countDownTimer);
      this.countDownTimer = null;
    }
  }

  /**
   * Resolve end time in ms. Prefer Liquid unix ms (timezone-safe).
   * @returns {number}
   */
  getEndMs() {
    if (this.endMs) return this.endMs;
    const parsed = new Date(this.endDate).getTime();
    return Number.isFinite(parsed) ? parsed : NaN;
  }

  /**
   * @returns {boolean} false when timer should stop
   */
  tick() {
    try {
      const endDateMilliseconds = this.getEndMs();
      if (!Number.isFinite(endDateMilliseconds)) {
        this.stopTimer();
        return false;
      }

      const difference = endDateMilliseconds - Date.now();
      if (difference < 0) {
        this.classList.add('d-none');
        this.removeAttribute('data-start_date');
        this.stopTimer();
        return false;
      }

      const day = Math.floor(difference / 86400000);
      const hour = Math.floor((difference % 86400000) / 3600000);
      const min = Math.floor((difference % 3600000) / 60000);
      const sec = Math.floor((difference % 60000) / 1000);

      if (!this.refs) this.refs = this.cacheRefs();

      if (this.refs.type === 'digit') {
        this.renderDigitBox(day, hour, min, sec);
      } else if (this.refs.type === 'badge') {
        this.renderBadge(day, hour, min);
      }

      return true;
    } catch (error) {
      console.warn('[custom-countdown]', error);
      this.stopTimer();
      return false;
    }
  }

  /**
   * Hero / section digit-box layout — unchanged markup contract.
   */
  renderDigitBox(day, hour, min, sec) {
    const { day1, day2, hour1, hour2, min1, min2, sec1, sec2 } = this.refs.nodes;
    const dStr = ('0' + day).slice(-2).split('');
    const hStr = ('0' + hour).slice(-2).split('');
    const mStr = ('0' + min).slice(-2).split('');
    const sStr = ('0' + sec).slice(-2).split('');

    if (day1) day1.textContent = dStr[0];
    if (day2) day2.textContent = dStr[1];
    if (hour1) hour1.textContent = hStr[0];
    if (hour2) hour2.textContent = hStr[1];
    if (min1) min1.textContent = mStr[0];
    if (min2) min2.textContent = mStr[1];
    if (sec1) sec1.textContent = sStr[0];
    if (sec2) sec2.textContent = sStr[1];
  }

  /**
   * Announcement-bar compact badge layout only.
   */
  renderBadge(day, hour, min) {
    const { badgeDays, badgeHours, badgeMins } = this.refs.nodes;
    badgeDays.textContent = String(day);
    badgeHours.textContent = String(hour);
    badgeMins.textContent = String(min);
  }
}

if (!customElements.get('custom-countdown')) {
  customElements.define('custom-countdown', Countdown);
}